import { sb } from '../supabase';
import { fetchVerificationQueue } from './consoleVerification';
import { fetchOpenViolationCount } from './consoleContactViolations';

// ── The review hub ─────────────────────────────────────────────────────────
//
// Everything waiting for an admin to approve, counted in one place. Three of
// the four things on this board are real queues with a waiting state; factory
// products are not, and the board says so rather than inventing one.
//
//   • supplier files  — a queue. bucket 'ready' means documents are in.
//   • catalog imports — a queue. status 'extracted'/'reviewing' means the
//                       extraction finished and nobody has accepted it yet.
//   • contact violations — a queue (since 2026-10-15). review_status 'open'
//                       until an admin dismisses or warns; see
//                       consoleContactViolations.js.
//   • factory products — NOT a queue. `factory_products` has no review state at
//                       all: no pending flag, no submitted_at, nothing that says
//                       "this one is new and unchecked". The design this hub was
//                       built from showed "31 products from 4 suppliers waiting",
//                       which cannot be computed from what exists. So the tile
//                       links to the QA tool and reports the catalog size, and
//                       the number it shows is labelled as what it is. Adding a
//                       real review state is a schema change and an owner's
//                       decision, not something to fake with a count.

const safe = (label, p) => p.then(
  (r) => {
    if (r?.error) console.error(`[consoleReviews] ${label} failed:`, r.error.message || r.error);
    return r;
  },
  (e) => { console.error(`[consoleReviews] ${label} threw:`, e?.message || e); return { data: null, count: 0, error: e }; },
);

export async function fetchReviewBoard() {
  const [queue, imports, products, violations] = await Promise.all([
    fetchVerificationQueue().catch((e) => {
      console.error('[consoleReviews] verification queue failed:', e?.message || e);
      return { rows: [], buckets: { ready: [], incomplete: [], not_started: [], verified: [], rejected: [] } };
    }),
    safe('catalog imports', sb.from('factory_catalog_imports')
      .select('id, factory_id, status, created_at')
      .in('status', ['extracted', 'reviewing'])
      .order('created_at', { ascending: true })),
    safe('factory products', sb.from('factory_products')
      .select('id', { count: 'exact', head: true })),
    // Contact violations waiting for a decision (owner, 2026-10-15) — a real
    // queue: review_status 'open' until an admin dismisses or warns.
    fetchOpenViolationCount().catch((e) => {
      console.error('[consoleReviews] contact violations failed:', e?.message || e);
      return null;
    }),
  ]);

  const ready = [...(queue.buckets.ready || [])]
    .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
  const imps = imports?.data || [];

  return {
    files: {
      count: ready.length,
      oldest: ready[0] || null,
      // The whole supplier queue, so the hub can say what is behind the number.
      byBucket: {
        ready: (queue.buckets.ready || []).length,
        incomplete: (queue.buckets.incomplete || []).length,
        not_started: (queue.buckets.not_started || []).length,
        verified: (queue.buckets.verified || []).length,
        rejected: (queue.buckets.rejected || []).length,
      },
    },
    catalogs: {
      count: imps.length,
      oldest: imps[0] || null,
      factories: new Set(imps.map((i) => i.factory_id).filter(Boolean)).size,
    },
    products: { total: products?.count || 0 },
    // null = could not be read; the tile says so instead of showing a false 0.
    violations: { count: violations },
  };
}
